import { memo, useCallback, useEffect, useRef, useState } from "react";

import { createPortal } from "react-dom";

import { useTranslation } from "react-i18next";

import { sileo } from "sileo";

import { useAtomValue } from "jotai";

import { FaExclamationTriangle } from "react-icons/fa";

import { Modal } from "@eriol-io/design-system";

import { Button } from "@/components/ui/button";

import { detectPlatform, isNewerVersion } from "@/Utils/version";

import { applyWebUpdate, checkForWebUpdate } from "@/Utils/serviceWorker";

import { atomOnLive } from "@/components/CustomTitleBar/CustomTitleBar.atoms";

import {
  fetchUpdateManifest,
  getCurrentShellVersion,
  isElectron,
  triggerNativeInstall,
  requiresWindowsInstaller,
  downloadWindowsInstaller,
  WINDOWS_INSTALLER_RECOVERY_EVENT,
} from "@/services/desktopUpdater";
import type { InstallerDownloadProgress } from "@/services/windowsInstallerDownload";

/** Cada cuánto se chequea el manifiesto combinado (web + nativo). */

const CHECK_INTERVAL_MS = 15 * 60 * 1000;

/** Delay inicial: no chequear apenas monta, dejar que la app termine de bootear. */

const BOOT_DELAY_MS = 10_000;

/**
 * Update-manager del desktop. Montado global (Root). Cada 15 min consulta el
 * endpoint conjunto y decide:
 *  - Hay update NATIVO (MSI) y estamos en Electron → diálogo de confirmación →
 *    al aceptar dispara updater.exe (la app se cierra). Prioridad sobre el web.
 *  - Solo hay update WEB → toast de sileo (mismo patrón que Eriol Go) → al
 *    aceptar aplica el SW nuevo y recarga. Nunca recarga solo (no corta un vivo).
 */

function DesktopUpdatePromptInner() {
  const { t } = useTranslation("settings");

  const [msiVersion, setMsiVersion] = useState<string | null>(null);

  // Carril macOS: versión ya DESCARGADA por electron-updater (mac-update:*).

  const [macVersion, setMacVersion] = useState<string | null>(null);

  const [installing, setInstalling] = useState(false);

  const [installError, setInstallError] = useState<string | null>(null);
  const [useInstallerDownload, setUseInstallerDownload] = useState(false);
  const [downloadProgress, setDownloadProgress] =
    useState<InstallerDownloadProgress | null>(null);
  const [downloadedFile, setDownloadedFile] = useState<string | null>(null);
  const downloadController = useRef<AbortController | null>(null);
  const installInFlight = useRef(false);

  // Dedupe: última versión ya avisada por canal, para no repetir cada tick.

  const promptedNative = useRef<string | null>(null);

  const promptedMac = useRef<string | null>(null);

  const promptedWeb = useRef<string | null>(null);

  const webToastId = useRef<string | null>(null);

  const onLive = useAtomValue(atomOnLive);

  // Ref para leer el estado de vivo ACTUAL dentro del onClick del toast (que

  // captura el scope de cuando se creó el toast, no el del click).

  const onLiveRef = useRef(onLive);

  onLiveRef.current = onLive;

  // Guard "no cortar un vivo": si hay transmisión activa, pedir confirmación

  // antes de recargar (web) o cerrar (nativo). Mismo criterio que el tab About.

  const confirmIfLive = useCallback((): boolean => {
    if (!onLiveRef.current) return true;

    return window.confirm(
      t(
        "SETTINGS.FORMS.ABOUT.liveWarning",

        "Estás transmitiendo. Si actualizás, la app se va a recargar/cerrar y se cortará la transmisión. ¿Seguro querés continuar?",
      ),
    );
  }, [t]);

  const showWebToast = useCallback(() => {
    if (webToastId.current) sileo.dismiss(webToastId.current);

    webToastId.current = sileo.action({
      title: t(
        "SETTINGS.FORMS.ABOUT.update.webTitle",

        "Actualización disponible",
      ),

      description: t(
        "SETTINGS.FORMS.ABOUT.update.webDescription",

        "Hay una versión nueva de Eriol. Actualizá cuando quieras.",
      ),

      duration: null,

      button: {
        title: t("SETTINGS.FORMS.ABOUT.update.webAction", "Actualizar"),

        onClick: () => {
          if (!confirmIfLive()) return;

          if (webToastId.current) {
            sileo.dismiss(webToastId.current);

            webToastId.current = null;
          }

          applyWebUpdate();
        },
      },
    });
  }, [t, confirmIfLive]);

  const runCheck = useCallback(async () => {
    if (installInFlight.current) return;
    if (
      typeof navigator !== "undefined" &&
      "onLine" in navigator &&
      !navigator.onLine
    ) {
      return;
    }

    const manifest = await fetchUpdateManifest();

    if (!manifest) return; // endpoint caído → fail-soft, no molestar

    const inElectron = isElectron();

    // Versión de build WEB (única por deploy, con epoch) inyectada por Vite

    // `define`. Fallback a la env por si el define no aplica (p.ej. tests). Un

    // cliente recién cargado NO se auto-ofrece update (appCurrent === version.json

    // del mismo build) y un cliente de un build viejo SÍ ve el toast.

    const appCurrent =
      typeof __APP_BUILD_VERSION__ !== "undefined"
        ? __APP_BUILD_VERSION__
        : (import.meta.env.VITE_APP_VERSION ?? null);

    const shellCurrent = inElectron ? await getCurrentShellVersion() : null;

    // En macOS el update nativo NO va por el manifiesto: lo maneja electron-

    // updater en el main (eventos mac-update:*, effect de abajo). La acción de

    // este carril dispara el updater.exe local de Windows, que en mac no existe.

    const nativeUpdate =
      inElectron &&
      detectPlatform() !== "macos" &&
      manifest.native != null &&
      isNewerVersion(shellCurrent, manifest.native.version);

    const appUpdate = isNewerVersion(appCurrent, manifest.application.version);

    // Prioridad: el nativo trae el shell/binarios y, al reinstalar, recarga el

    // web fresco. Si hay ambos, mandamos PRIMERO el diálogo MSI (no el toast).

    // Pero los carriles son independientes: un nativo ya avisado (y quizás

    // descartado) NO suprime el toast web — se ofrece en el próximo tick.

    if (nativeUpdate && manifest.native) {
      if (promptedNative.current !== manifest.native.version) {
        promptedNative.current = manifest.native.version;

        // En el tick que estrena el nativo, este supersede al web: descartamos

        // cualquier toast web pendiente y no ofrecemos web hasta el siguiente.

        if (webToastId.current) {
          sileo.dismiss(webToastId.current);

          webToastId.current = null;
        }

        setMsiVersion(manifest.native.version);
        setUseInstallerDownload(requiresWindowsInstaller(shellCurrent));

        return;
      }

      // Nativo ya avisado esta sesión: seguimos y evaluamos el carril web.
    }

    if (appUpdate && manifest.application.version) {
      if (promptedWeb.current === manifest.application.version) return;

      promptedWeb.current = manifest.application.version;

      // Forzamos al SW a precachear el bundle nuevo antes de ofrecer "Actualizar".

      await checkForWebUpdate();

      showWebToast();
    }
  }, [showWebToast]);

  useEffect(() => {
    // La ventana OSR/broadcast no muestra prompts de update.

    const isBroadcast =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("mode") === "broadcast";

    if (isBroadcast) return;

    const boot = setTimeout(() => void runCheck(), BOOT_DELAY_MS);

    const interval = setInterval(() => void runCheck(), CHECK_INTERVAL_MS);

    return () => {
      clearTimeout(boot);

      clearInterval(interval);

      if (webToastId.current) {
        sileo.dismiss(webToastId.current);

        webToastId.current = null;
      }
    };
  }, [runCheck]);

  // Carril macOS: el main (macAutoUpdater) chequea solo y emite mac-update:*.

  //  - available → disparamos la descarga en background (autoDownload=false en

  //    el main: la decisión vive acá). Durante un vivo NO descargamos para no

  //    competir con el bitrate; el chequeo periódico del main reintenta después.

  //  - downloaded → mismo modal nativo que el MSI, pero la acción instala vía

  //    IPC (quitAndInstall) en vez del updater.exe de Windows.

  // Feature-detect con optional chaining: shells sin el preload nuevo no

  // exponen estos métodos y el carril queda inerte (sigue autoInstallOnAppQuit).

  useEffect(() => {
    const isBroadcast =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("mode") === "broadcast";

    if (isBroadcast || detectPlatform() !== "macos") return;

    const api = typeof window !== "undefined" ? window.electronAPI : undefined;

    const offAvailable = api?.onMacUpdateAvailable?.(() => {
      if (onLiveRef.current) return;

      void (async () => {
        try {
          const res = await api?.macUpdateDownload?.();

          if (res && !res.success) {
            console.warn("[updater] mac downloadUpdate falló:", res.error);
          }
        } catch (err) {
          console.warn("[updater] mac downloadUpdate falló:", err);
        }
      })();
    });

    const offDownloaded = api?.onMacUpdateDownloaded?.((data) => {
      const version = data?.version ?? "unknown";

      if (promptedMac.current === version) return;

      promptedMac.current = version;

      setMacVersion(version);
    });

    return () => {
      offAvailable?.();

      offDownloaded?.();
    };
  }, []);

  const dismissNativePrompt = useCallback(() => {
    downloadController.current?.abort();
    downloadController.current = null;
    setDownloadProgress(null);
    setDownloadedFile(null);
    setInstallError(null);

    setMsiVersion(null);

    setMacVersion(null);
  }, []);

  useEffect(() => {
    const showRecovery = (event: Event) => {
      if (
        !isElectron() ||
        detectPlatform() !== "windows" ||
        new URLSearchParams(window.location.search).get("mode") === "broadcast"
      )
        return;
      const version = (event as CustomEvent<{ version?: unknown }>).detail
        ?.version;
      if (typeof version !== "string" || installInFlight.current) return;
      setMsiVersion(version);
      setUseInstallerDownload(true);
      setInstallError(null);
      setDownloadedFile(null);
    };
    window.addEventListener(WINDOWS_INSTALLER_RECOVERY_EVENT, showRecovery);
    return () => {
      window.removeEventListener(
        WINDOWS_INSTALLER_RECOVERY_EVENT,
        showRecovery,
      );
      downloadController.current?.abort();
    };
  }, []);

  const handleInstallNative = async () => {
    if (installInFlight.current) return;

    if (!useInstallerDownload && !confirmIfLive()) return;

    setInstallError(null);
    setDownloadProgress(null);
    setDownloadedFile(null);

    setInstalling(true);
    installInFlight.current = true;
    const controller = new AbortController();
    downloadController.current = controller;

    try {
      if (macVersion !== null) {
        // macOS: quitAndInstall del macAutoUpdater vía IPC. La app se cierra

        // sola para instalar (mismo contrato que el carril MSI).

        const res = await window.electronAPI?.macUpdateInstall?.();

        if (!res?.success) {
          throw new Error(res?.error ?? "mac-update:install no disponible");
        }
      } else {
        if (useInstallerDownload) {
          const result = await downloadWindowsInstaller({
            signal: controller.signal,
            onProgress: setDownloadProgress,
          });
          if (!controller.signal.aborted) setDownloadedFile(result.fileName);
          return;
        } else await triggerNativeInstall();
      }

      // La app se cierra sola para instalar; dejamos el modal cerrado.

      dismissNativePrompt();
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error("[updater] download-and-install falló:", err);

      setInstalling(false);

      setInstallError(err instanceof Error ? err.message : String(err));
    } finally {
      installInFlight.current = false;
      setInstalling(false);
      if (downloadController.current === controller)
        downloadController.current = null;
    }
  };

  return createPortal(
    <Modal
      isOpen={msiVersion !== null || macVersion !== null}
      title="Eriol"
      onClose={dismissNativePrompt}
      showCloseButton={!installing}
      footer={
        <div className="flex justify-end gap-3">
          {!downloadedFile && (
            <Button
              variant="outline"
              onClick={dismissNativePrompt}
              disabled={installing && !useInstallerDownload}
            >
              {installing && useInstallerDownload
                ? "Cancelar descarga"
                : t("SETTINGS.FORMS.ABOUT.update.later", "Más tarde")}
            </Button>
          )}
          <Button
            onClick={downloadedFile ? dismissNativePrompt : handleInstallNative}
            disabled={installing}
          >
            {downloadedFile
              ? "Cerrar"
              : installing && useInstallerDownload
                ? "Descargando..."
                : installing
                  ? t("SETTINGS.FORMS.ABOUT.update.installing", "Iniciando…")
                  : useInstallerDownload
                    ? t(
                        "SETTINGS.FORMS.ABOUT.update.downloadInstaller",
                        "Descargar instalador",
                      )
                    : t("SETTINGS.FORMS.ABOUT.update.install", "Instalar")}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 text-foreground">
        {useInstallerDownload && downloadProgress && !downloadedFile && (
          <div role="status" className="space-y-2 text-sm">
            <p>
              {downloadProgress.phase === "verifying"
                ? "Verificando descarga..."
                : "Descargando actualizacion..."}
            </p>
            <progress
              className="h-2 w-full"
              aria-label="Descarga del instalador"
              value={downloadProgress.loaded}
              max={downloadProgress.total}
            />
            <p>
              {(downloadProgress.loaded / 1048576).toFixed(1)} /{" "}
              {(downloadProgress.total / 1048576).toFixed(1)} MB
            </p>
          </div>
        )}
        {downloadedFile && (
          <div role="status" className="space-y-2 text-sm">
            <p className="font-semibold">Instalador verificado</p>
            <p className="break-words">{downloadedFile}</p>
            <p>
              Abri el archivo guardado para actualizar la instalacion existente.
            </p>
          </div>
        )}
        {installError !== null && (
          <div role="alert" className="text-sm text-destructive break-words">
            <p className="font-semibold">
              {t("SETTINGS.FORMS.ABOUT.update.error")}
            </p>
            <p>{installError}</p>
          </div>
        )}
        <div className="flex items-start gap-3">
          <div className="text-yellow-400 mt-1">
            <FaExclamationTriangle size={24} />
          </div>
          <div className="flex flex-col gap-3">
            <p className="font-semibold text-base">
              {t(
                "SETTINGS.FORMS.ABOUT.update.nativeTitle",

                "Hay una nueva versión de la app de escritorio",
              )}
            </p>
            <div className="text-sm text-muted-foreground space-y-2">
              {useInstallerDownload ? (
                <p>
                  {t(
                    "SETTINGS.FORMS.ABOUT.update.installerRecovery",
                    "Esta versión necesita actualizarse con el instalador. Abrí el archivo descargado para completar la actualización; tus datos se conservan.",
                  )}
                </p>
              ) : (
                <p>
                  <strong className="text-yellow-400">IMPORTANTE:</strong>{" "}
                  {t(
                    "SETTINGS.FORMS.ABOUT.update.nativeWarning",

                    "La aplicación se cerrará automáticamente cuando comience la instalación.",
                  )}
                </p>
              )}
              <p>
                {t(
                  "SETTINGS.FORMS.ABOUT.update.nativeDuration",

                  "El proceso de actualización tomará unos minutos.",
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>,

    document.body,
  );
}

export const DesktopUpdatePrompt = memo(DesktopUpdatePromptInner);
